import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "./auth";
import { logPageView } from "./traffic";

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

/**
 * Per-module page-traffic logging (writes to revq-lumina-traffic.page_views).
 *
 * Measures time-until-records-on-screen and total records returned, keyed by
 * the logged-in email (encrypted server-side). Distinguishes the first landing
 * ("initial") from an in-page search re-feed ("search", with its criteria).
 *
 *   const { report, markSearch } = usePageTraffic("encounter-review");
 *   report(rows.length);              // when a result set paints
 *   markSearch({ q, dateFrom });      // just before an in-page search re-fetch
 *
 * On mount many pages fire several loads as derived state settles. Those are
 * collapsed into ONE "initial" event with the final record count (quiet-period
 * debounce), and any markSearch during that window is treated as settling, not
 * a user search — so only genuine re-feeds after the page settles are logged.
 */
export function usePageTraffic(module) {
  const { user } = useAuth();
  const email = user?.email || "";
  const mountRef = useRef(now());
  const committed = useRef(false);     // initial event has been sent
  const initCount = useRef(0);         // latest record count seen while settling
  const initEnd = useRef(0);           // time of the last settling report (≈ paint)
  const timer = useRef(null);
  const searchPending = useRef(null);  // { t0, criteria } once a real search fires

  const emailRef = useRef(email);
  emailRef.current = email;
  const moduleRef = useRef(module);
  moduleRef.current = module;

  const send = useCallback((event, count, criteria, t0, t1) => {
    logPageView({
      email: emailRef.current,
      module: moduleRef.current,
      event,
      duration_ms: Math.max(0, Math.round((t1 || now()) - t0)),
      record_count: typeof count === "number" ? count : 0,
      search_criteria: criteria || null,
    });
  }, []);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const report = useCallback((count) => {
    if (!committed.current) {
      // Still settling → keep the latest count; commit once loads go quiet.
      initCount.current = typeof count === "number" ? count : 0;
      initEnd.current = now();
      searchPending.current = null;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        committed.current = true;
        send("initial", initCount.current, null, mountRef.current, initEnd.current);
      }, 900);
      return;
    }
    if (searchPending.current) {
      const { t0, criteria } = searchPending.current;
      searchPending.current = null;
      send("search", count, criteria, t0, now());
    }
  }, [send]);

  const markSearch = useCallback((criteria) => {
    if (!committed.current) return;   // mount-time settling, not a real search
    searchPending.current = { t0: now(), criteria: criteria || {} };
  }, []);

  return { report, markSearch };
}
